import { belongsToGroup, prefixesFor } from './modelPrefixes';
import type { TModelGroup } from './modelSpellings';
import generated from './modelIndex.generated.json';
import overridesFile from './modelIndex.overrides.json';

/**
 * The model index — which spellings mean the same car (spec 009).
 *
 * Both auction houses spell one model many ways: nineteen for a Ford F-150,
 * twenty-six for a Mercedes E. A model page has to query all of them or it shows
 * a fraction of the cars.
 *
 * That reconciliation USED to happen on every request, from string heuristics.
 * Two defects — `/aukcje/toyota/gr86` missing `GR 86 PREM`, `/aukcje/toyota/supra`
 * missing `GR SUPRA` — were both found by a person looking at the site rather
 * than by any check, and each fix took two attempts. Which spellings mean the
 * same car is domain knowledge, so it is now DATA somebody approves rather than
 * a regex somebody tunes after each complaint.
 *
 * Two files, and the split is the guarantee:
 *  - `modelIndex.generated.json` — rewritten by the sweep, never hand-edited.
 *  - `modelIndex.overrides.json` — written only by people; the sweep has no code
 *    path that writes it, so a reviewed decision cannot be lost to a bug in it.
 *
 * Read at module load, not per request: it is a committed file, so there is
 * nothing to await and no failure mode beyond the build itself.
 */

type TPrefixEntry = { value: string; yield: { onGroup: number; returned: number } | null };

type TGeneratedGroup = {
  canonical: string;
  spellings: string[];
  sampled: boolean;
  /** Written by the sweep (spec 011). Absent on an index built before it. */
  prefixes?: TPrefixEntry[];
  spellingStatus?: Record<string, string | undefined>;
};

type TOverride =
  | { op: 'attach'; make: string; spelling: string; group: string; reason?: string }
  | { op: 'detach'; make: string; spelling: string; reason?: string }
  | { op: 'canonical'; make: string; group: string; name: string; reason?: string };

const generatedGroups = (generated as { groupsByMake?: Record<string, TGeneratedGroup[]> })
  .groupsByMake ?? {};

const overrides = ((overridesFile as { overrides?: unknown }).overrides ?? []) as TOverride[];

/**
 * Applies corrections on top of the generated groups, in FILE ORDER.
 *
 * The sweep applies these too, and checks the result — so anything reaching here
 * has already passed the invariants. This exists because the app must see the
 * same effective index the sweep verified, without re-running the sweep at build
 * time.
 */
const applyOverrides = (make: string, groups: TGeneratedGroup[]): TGeneratedGroup[] => {
  const relevant = overrides.filter(o => o.make?.toUpperCase() === make);

  if (!relevant.length) return groups;

  const next = groups.map(g => ({ ...g, spellings: [...g.spellings] }));

  for (const o of relevant) {
    if (o.op === 'attach') {
      const to = next.find(g => g.canonical === o.group);

      if (!to) continue;

      next.forEach(g => {
        g.spellings = g.spellings.filter(s => s !== o.spelling);
      });

      to.spellings.push(o.spelling);
    } else if (o.op === 'detach') {
      const from = next.find(g => g.spellings.includes(o.spelling));

      if (!from) continue;

      from.spellings = from.spellings.filter(s => s !== o.spelling);
      next.push({ canonical: o.spelling, spellings: [o.spelling], sampled: false });
    } else if (o.op === 'canonical') {
      const g = next.find(entry => entry.canonical === o.group);

      if (g) g.canonical = o.name;
    }
  }

  return next.filter(g => g.spellings.length > 0);
};

const cache = new Map<string, TGeneratedGroup[]>();

/** Groups with everything the sweep wrote, including measured prefix yields. */
const rawGroupsForMake = (make: string): TGeneratedGroup[] => {
  const key = make.toUpperCase();
  const cached = cache.get(key);

  if (cached) return cached;

  const groups = applyOverrides(key, generatedGroups[key] ?? []);

  cache.set(key, groups);

  return groups;
};

/** Every model group for a make, canonical name and all its spellings. */
export const groupsForMake = (make: string): TModelGroup[] =>
  rawGroupsForMake(make).map(({ canonical, spellings }) => ({ canonical, spellings }));

const normalise = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Every spelling to query for a requested model.
 *
 * Order is meaningful: the caller applies a request budget and takes the first
 * few, so the most productive spellings must come first. Until inventory
 * sampling lands (spec 009 US3) that order is shortest-first, which the
 * generated file marks as unsampled.
 *
 * Falls back to the requested string alone when nothing matches, so an unknown
 * or hand-typed model still returns whatever the provider has rather than
 * nothing at all.
 */
export const spellingsForModel = (make: string, requested: string): string[] => {
  const wanted = normalise(requested);
  const groups = groupsForMake(make);

  const match =
    groups.find(group => normalise(group.canonical) === wanted) ??
    groups.find(group => group.spellings.some(spelling => normalise(spelling) === wanted));

  return match?.spellings ?? [requested];
};

/**
 * The canonical name for a raw catalogue spelling, or null when the index has
 * never seen it.
 *
 * Used to build a lot's ADDRESS (spec 010): the model segment has to be a value
 * `/aukcje/<marka>/<model>` can resolve, and the group canonical is that value.
 * Without it, `330I`, `330I SULEV` and a truncated `330I SU` would each mint a
 * separate address for the same model level — three URLs, one car.
 *
 * Returns null rather than the input so the caller decides the fallback; an
 * unknown spelling still resolves as a model page through `resolveModelSlug`,
 * which matches spellings as well as canonicals.
 */
export const groupForSpelling = (make: string, spelling: string): string | null => {
  const wanted = normalise(spelling);

  if (!wanted) return null;

  const groups = groupsForMake(make);

  const match =
    groups.find(group => normalise(group.canonical) === wanted) ??
    groups.find(group => group.spellings.some(entry => normalise(entry) === wanted));

  return match?.canonical ?? null;
};

/**
 * The prefixes to query for a requested model (spec 011).
 *
 * REPLACES THE SPELLING FAN-OUT for any group that has one. `spellingsForModel`
 * hands the caller up to nineteen exact names, of which only six are ever asked
 * for and several may match nothing at all — measured, five of the six requests
 * an X5 page spends return zero rows, because `X5 XDRIVE4` and `X5 SDRIVE3` are
 * the vendor catalogue's TRUNCATIONS of names real lots spell in full. One
 * `q=X5` reaches all fifteen spellings in a single request.
 *
 * Derived at request time from the spellings already in the committed index —
 * no new file, no sweep run, nothing to migrate. Pure string work over data
 * that is read once at module load.
 *
 * RETURNS AN EMPTY ARRAY as a real answer, not a failure: it means "ask for this
 * group by exact spellings instead". Thirty-six groups do, Mercedes-Benz E/S/C
 * among them, because every one of their spellings is a single letter followed
 * by a space. Those keep the exact fan-out until the sweep has measured whether
 * a one-character prefix is worth a request — it is for Mercedes (`q=E` reaches
 * 19 of 20 rows on-group) and worthless for BMW (`q=3` reaches 0 of 20).
 */
export const prefixesForModel = (make: string, requested: string): string[] => {
  const wanted = normalise(requested);
  const groups = groupsForMake(make);

  const match =
    groups.find(group => normalise(group.canonical) === wanted) ??
    groups.find(group => group.spellings.some(spelling => normalise(spelling) === wanted));

  // An unknown or hand-typed model has no group, so there is nothing to widen —
  // the caller falls back to the exact string, as it does today.
  if (!match) return [];

  /*
   * A PREFIX THAT IS ALSO A MAKE NAME IS DISCARDED, and this one cost a page.
   *
   * `q` matches the model as a prefix OR THE MAKE EXACTLY. When the value
   * happens to be a make, the make branch appears to win and the model branch
   * is abandoned — so `make=CHEVROLET&q=MALIBU` becomes an impossible "Chevrolet
   * AND Malibu" and returns ZERO. Measured: `q=MALIBU` → 0 rows while
   * `q=MALIB` → 20 including `MALIBU` and `MALIBU HYBRID`, and `model=MALIBU`
   * → 20. Same for `q=TAHOE`.
   *
   * Malibu and Tahoe are boat and trailer manufacturers as well as Chevrolets,
   * and the vendor lists both as makes. 380 model canonicals in this index
   * collide with a make name — mostly trailer and boat noise, but the Chevrolet
   * Malibu and Tahoe are among the most-searched cars on the site, and both
   * rendered an EMPTY page before this filter.
   *
   * Dropping the prefix sends the group down the exact fan-out, which is what it
   * used to do and which works. The safety net in `getAuctionLots` catches
   * anything this does not anticipate.
   */
  const makes = new Set(indexedMakes().map(entry => entry.toUpperCase()));
  const usable = prefixesFor(match.spellings).filter(prefix => !makes.has(prefix.toUpperCase()));

  if (usable.length > 0) return usable;

  /*
   * LAST RESORT: a one-character prefix, but only where the sweep MEASURED that
   * it finds this group's cars (spec 011, R9).
   *
   * These are the 36 groups whose every spelling is a letter, a space and a
   * number — Mercedes-Benz E-Class (26 spellings), S-Class (22), C-Class (20)
   * and BMW's `n SERIES`. Without this they spend six requests on the exact
   * fan-out; with it, one.
   *
   * IT CANNOT BE DECIDED BY RULE, which is the whole point. Measured against
   * live inventory: `q=E` on Mercedes-Benz returns 19 of 20 rows on-group,
   * because there the letter IS the model family. `q=3` on BMW returns twenty
   * rows of which NOT ONE belongs to `3 SERIES`, because the digit is shared
   * with a dozen sibling models that are genuinely different cars. Nothing in
   * the string distinguishes those two cases.
   *
   * So the app reads evidence rather than deriving it, and NO EVIDENCE MEANS NO
   * PERMISSION: a group the sweep never sampled keeps the exact fan-out, which
   * is exactly what it does today. The failure mode is the status quo.
   */
  const raw = rawGroupsForMake(make).find(group => group.canonical === match.canonical);

  const proven = (raw?.prefixes ?? [])
    .filter(entry => (entry.yield?.onGroup ?? 0) > 0 && !makes.has(entry.value.toUpperCase()))
    .map(entry => entry.value);

  return proven;
};

/**
 * Does a lot returned by a prefix query belong on the requested model's page?
 *
 * The precision half of spec 011, bound to the committed index. Without it a
 * prefix query is a regression rather than a fix: `q=F` on Ford returns
 * `FUSION`, `FOCUS` and `F-750`, and `q=MX` on Mazda returns `MX-3`.
 *
 * The rule itself lives in `modelPrefixes.ts` so the sweep applies exactly the
 * same one when it measures how productive a prefix is.
 */
export const isModelInGroup = (make: string, requestedCanonical: string, lotModel: string): boolean =>
  belongsToGroup({
    requestedCanonical,
    lotModel,
    resolveGroup: spelling => groupForSpelling(make, spelling),
  });

/** Every make the index knows, for slug resolution. */
export const indexedMakes = (): string[] => Object.keys(generatedGroups);

/** Whether the index holds anything at all — a build that shipped without it. */
export const hasModelIndex = (): boolean => Object.keys(generatedGroups).length > 0;
